import { FC, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Charts } from '@/_shared/components/Charts';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { getEmployeesMeCall } from '@/profile';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  postEmployerByIdSurveyResultCall,
} from '@/surveys/state';

type Props = {
  surveyTemplateId: string | undefined;
};

export const SurveyResultsVsOrganization: FC<Props> = ({ surveyTemplateId }) => {
  const t = useTranslations('assessments.resultsVsOrganization');
  const tShared = useTranslations();

  const { genericResponsiveValue } = useResponsiveStyle();

  const {
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    departmentsStats,
    organizationStats,
    loading: loadingGetSurveyCampaignByIdResponsesResultsStats,
    error: errorGetSurveyCampaignByIdResponsesResultsStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const { response: responseGetEmployeesMe } = getEmployeesMeCall.hook();

  const {
    response: responsePostEmployerByIdSurveyResult,
    success: successPostEmployerByIdSurveyResult,
  } = postEmployerByIdSurveyResultCall.hook();

  const data = useMemo(() => {
    if (successPostEmployerByIdSurveyResult) {
      if (!departmentsStats?.[0].metricsScore || !responsePostEmployerByIdSurveyResult) return [];

      const employeeMetricsMap = new Map<string, number>();
      responsePostEmployerByIdSurveyResult.forEach(metric => {
        if (Array.isArray(metric?.results?.results)) {
          metric.results.results.forEach(result => {
            if (result.metric?.id) {
              employeeMetricsMap.set(result.metric.id, result.score || 0);
            }
          });
        }
      });

      const orgMetricsMap = new Map<string, number>();
      organizationStats?.metricsScore?.forEach(orgMetric => {
        if (orgMetric.metric?.id) {
          orgMetricsMap.set(orgMetric.metric.id, orgMetric.score || 0);
        }
      });

      return departmentsStats?.[0].metricsScore.map(depMetric => {
        const valueEmployee = employeeMetricsMap.get(depMetric.metric?.id || '') || 0;
        const valueOrg = orgMetricsMap.get(depMetric.metric?.id || '') || 0;
        const diff = valueEmployee - Number(depMetric?.score);
        const diffOrg = valueEmployee - Number(valueOrg);
        // eslint-disable-next-line no-nested-ternary
        const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
        // eslint-disable-next-line no-nested-ternary
        const signOrg = diffOrg > 0 ? '+' : diffOrg < 0 ? '-' : '';

        return {
          name: depMetric.metric?.name || '',
          value: valueEmployee,
          label:
            t('labelTooltipChart', {
              current: valueEmployee,
              depValue: `${sign}${Math.abs(diff).toFixed(1)}`,
              orgValue: `${signOrg}${Math.abs(diffOrg).toFixed(1)}`,
            }) || '',
          value2: depMetric.score || 0,
          value3: valueOrg,
        };
      });
    }
    return [];
  }, [
    departmentsStats,
    organizationStats?.metricsScore,
    responsePostEmployerByIdSurveyResult,
    successPostEmployerByIdSurveyResult,
    t,
  ]);

  useEffect(() => {
    if (!surveyTemplateId) {
      return;
    }

    const campaignId =
      responsePostEmployerByIdSurveyResult &&
      responsePostEmployerByIdSurveyResult.find(survey => survey.templateId === surveyTemplateId)
        ?.results?.campaignId;

    if (campaignId === undefined) {
      return;
    }
    executeGetSurveyCampaignByIdResponsesResultsStats({
      campaignId,
      departmentId: responseGetEmployeesMe?.department?.id,
    });
  }, [
    executeGetSurveyCampaignByIdResponsesResultsStats,
    responseGetEmployeesMe,
    responsePostEmployerByIdSurveyResult,
    surveyTemplateId,
  ]);

  if (loadingGetSurveyCampaignByIdResponsesResultsStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetSurveyCampaignByIdResponsesResultsStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (
    surveyTemplateId &&
    responseGetEmployeesMe !== undefined &&
    Array.isArray(departmentsStats?.[0]?.metricsScore) &&
    departmentsStats?.[0].metricsScore.length > 0
  ) {
    return (
      <Box
        mt={genericResponsiveValue({
          breakPoints: { desktop: 'xl' },
          defaultValue: 'xs',
        })}
      >
        <Typography variant='h3'>{t('title')}</Typography>
        <Typography variant='p3/light'>{t('description')}</Typography>
        <Box mt='xxl' display='flex' flexDirection='row' alignItems='stretch'>
          <Box flex='1' mr='s' display='flex' alignItems='center'>
            <Charts.BarChartComponent
              domain={[0, 7]}
              hasMultipleValues
              domainHasInPercentage={false}
              data={data}
              defaultItemsSize={genericResponsiveValue({
                breakPoints: {
                  mobile: 2,
                  tablet: 3,
                },
                defaultValue: 6,
              })}
              startYAxisFrom='xs'
            />
          </Box>
        </Box>
        <Box mt='s' display='flex' justifyContent='center'>
          <Box
            justifyContent='start'
            width='100%'
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
          >
            <Box alignContent='center' display='flex' alignItems='center' mr='m'>
              <Box
                width='xxs'
                height='xxs'
                backgroundColor='primary'
                borderRadius='50%'
                mr='xxxs'
              />
              <Typography variant='p2/light'>{t('legend.yourScore')}</Typography>
            </Box>
            <Box alignContent='center' display='flex' alignItems='center' mr='m'>
              <Box
                width='xxs'
                height='xxs'
                backgroundColor={'rgba(176, 128, 255, 1)' as never}
                borderRadius='50%'
                mr='xxxs'
              />
              <Typography variant='p2/light'>
                {t('legend.depAverage', {
                  depName: responseGetEmployeesMe?.department?.name || '',
                  total: departmentsStats?.[0]?.totalCompletedSurveys?.toString() || '',
                })}
              </Typography>
            </Box>
            <Box alignContent='center' display='flex' alignItems='center' mr='m'>
              <Box width='xxs' height='xxs' backgroundColor='white' borderRadius='50%' mr='xxxs' />
              <Typography variant='p2/light'>
                {t('legend.orgAverage', {
                  total: organizationStats?.totalCompletedSurveys?.toString() || '',
                })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};
